import {
  ensureCounterClockwise,
  insetPolygon,
  localToWorld,
  polygonArea,
  type Obb2,
  type Vec2,
} from '@indusketch/geometry';
import type { Building, HallFloor, RoofType, Wall } from './types.js';
import { modelTexts } from './texts.js';

/** Sandwich panels of a typical hall are about 15 cm thick. */
export const DEFAULT_WALL_THICKNESS = 0.15;
export const DEFAULT_BUILDING_COLOR = '#c8ccd2';

/** Typical industrial hall: vertical sandwich panels, anthracite trims, light grey gates. */
export const DEFAULT_FACADE = {
  facade: 'iso-vertical',
  trimColor: '#4a5059',
  gateColor: '#cdd2d7',
} as const satisfies Pick<Building, 'facade' | 'trimColor' | 'gateColor'>;

/** Leaf of doors without an own colour: light grey steel. */
export const DEFAULT_DOOR_LEAF_COLOR = '#e9e7e1';

/** Roof covering unless one was chosen: light membrane on flat roofs, grey trapezoidal sheet. */
export const DEFAULT_ROOF_COLOR = { pitched: '#b3bac2', flat: '#dfe2df' } as const;

export const roofColorOf = (b: Pick<Building, 'roofColor' | 'roofType' | 'roofPitchDeg'>): string =>
  b.roofColor ??
  (b.roofType === 'flat' || b.roofPitchDeg === 0
    ? DEFAULT_ROOF_COLOR.flat
    : DEFAULT_ROOF_COLOR.pitched);

export const hallFloorOf = (b: Pick<Building, 'floorFinish'>): HallFloor =>
  b.floorFinish ?? 'concrete';

/** Side order matches the counter-clockwise wall order south → east → north → west. */
export const BUILDING_SIDES = ['south', 'east', 'north', 'west'] as const;
export type BuildingSide = (typeof BUILDING_SIDES)[number];

/**
 * Walls generated by a hall or room get deterministic ids derived from the owner id.
 * Regenerating walls after a size change therefore keeps the ids that openings refer to.
 */
export const ownedWallId = (ownerId: string, sideIndex: number): string =>
  `${ownerId}-w${sideIndex}`;
export const buildingWallId = ownedWallId;

/**
 * Exterior wall of one storey of an office along edge `edge` of that storey's outline. The
 * ground floor keeps the plain building wall ids, so openings there keep their wall.
 */
export const storeyWallId = (ownerId: string, level: number, edge: number): string =>
  level === 0 ? ownedWallId(ownerId, edge) : `${ownerId}-f${String(level)}-w${String(edge)}`;

/** Storey and edge of an exterior wall of building `b` (by its id), or null. */
export function storeyWallOf(
  b: Pick<Building, 'id'>,
  wallId: string,
): { level: number; edge: number } | null {
  const prefix = `${b.id}-`;
  if (!wallId.startsWith(prefix)) return null;
  const m = /^(?:f(\d+)-)?w(\d+)$/.exec(wallId.slice(prefix.length));
  if (!m) return null;
  return { level: m[1] === undefined ? 0 : Number(m[1]), edge: Number(m[2]) };
}

/**
 * Own outline of storey `level` of an office (Staffelgeschoss): that of the highest setback at
 * or below it; null when the storey has the building's outline.
 */
export function storeyOutline(b: Pick<Building, 'office'>, level: number): Vec2[] | null {
  if (!b.office || level <= 0) return null;
  let best: { level: number; outline: readonly Vec2[] } | null = null;
  for (const s of b.office.setbacks)
    if (s.level <= level && (!best || s.level > best.level)) best = s;
  return best ? best.outline.map((p) => ({ x: p.x, y: p.y })) : null;
}

/**
 * Ground floor sides without their exterior wall: removed on purpose (an office's ground floor
 * piece counts); the slab above ends at the wall's inner face there.
 */
export const removedSides = (
  b: Pick<Building, 'omittedWalls'> & { readonly office?: Building['office'] },
): number[] => [
  ...new Set([
    ...b.omittedWalls,
    ...(b.office?.openFloors.filter((f) => f.level === 0).map((f) => f.wall) ?? []),
  ]),
];

/** Outer outline of storey `level`: its own or the building's. */
export const storeyCorners = (b: Building, level: number): Vec2[] =>
  storeyOutline(b, level) ?? buildingCorners(b);

/** Wall centrelines of storey `level`, one per edge of that storey's outline. */
export function storeyWallGeometry(b: Building, level: number): WallGeometry[] {
  const own = storeyOutline(b, level);
  return own ? outlineWallGeometry(own, b.wallThickness) : buildingWallGeometry(b);
}

/** Floor elevations of an office's storeys and its top (same as `levelElevations`). */
function storeyElevations(o: NonNullable<Building['office']>): number[] {
  const out = [0];
  for (const f of o.floors) out.push((out[out.length - 1] ?? 0) + f.clearHeight + o.slabThickness);
  return out;
}

export const SIDE_LABELS: Readonly<Record<BuildingSide, string>> = {
  get south() {
    return modelTexts().sides.south;
  },
  get east() {
    return modelTexts().sides.east;
  },
  get north() {
    return modelTexts().sides.north;
  },
  get west() {
    return modelTexts().sides.west;
  },
};

export interface RectPlacement {
  readonly center: Vec2;
  readonly width: number;
  readonly depth: number;
  readonly rotation: number;
}

export function buildingObb(b: Pick<Building, 'center' | 'width' | 'depth' | 'rotation'>): Obb2 {
  return { center: b.center, width: b.width, depth: b.depth, rotation: b.rotation };
}

type Outlined = Pick<Building, 'center' | 'width' | 'depth' | 'rotation'> & {
  readonly footprint?: Building['footprint'];
};

/**
 * Outer footprint corners, counter-clockwise: the drawn outline of a free hall, otherwise the
 * rectangle starting at the south-west corner.
 */
export function buildingCorners(b: Outlined): Vec2[] {
  if (b.footprint) return b.footprint.map((p) => ({ x: p.x, y: p.y }));
  const hw = b.width / 2;
  const hd = b.depth / 2;
  const placement = { position: b.center, rotation: b.rotation };
  return [
    { x: -hw, y: -hd },
    { x: hw, y: -hd },
    { x: hw, y: hd },
    { x: -hw, y: hd },
  ].map((p) => localToWorld(p, placement));
}

export const buildingArea = (
  b: Pick<Building, 'width' | 'depth'> & { readonly footprint?: Building['footprint'] },
): number => (b.footprint ? polygonArea(b.footprint) : b.width * b.depth);

/** Clear interior floor area inside the exterior walls. */
export const buildingInteriorArea = (
  b: Pick<Building, 'width' | 'depth' | 'wallThickness'> & {
    readonly footprint?: Building['footprint'];
  },
): number =>
  b.footprint
    ? polygonArea(insetPolygon(b.footprint, b.wallThickness))
    : Math.max(0, b.width - 2 * b.wallThickness) * Math.max(0, b.depth - 2 * b.wallThickness);

/** Axis-aligned box around a drawn outline: the center, width and depth kept with it. */
export function outlineBox(points: readonly Vec2[]): RectPlacement {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
    width: Math.max(maxX - minX, 0.01),
    depth: Math.max(maxY - minY, 0.01),
    rotation: 0,
  };
}

interface WallGeometry {
  readonly start: Vec2;
  readonly end: Vec2;
}

/**
 * Wall centerlines such that the outer faces coincide exactly with the footprint:
 * south/north walls span the full width, east/west walls fit in between. This avoids
 * overlapping or missing corner volumes without any boolean geometry.
 */
export function buildingWallGeometry(b: Building): WallGeometry[] {
  if (b.footprint) return outlineWallGeometry(b.footprint, b.wallThickness);
  return rectangleWallGeometry(b, b.wallThickness);
}

/**
 * One wall per edge of a drawn outline: centrelines half a wall thickness inside, meeting in
 * mitred corners. Wall i runs along outline edge i (from point i to point i + 1).
 */
export function outlineWallGeometry(outline: readonly Vec2[], t: number): WallGeometry[] {
  const inner = insetPolygon(ensureCounterClockwise(outline), t / 2);
  return inner.map((start, i) => ({ start, end: inner[(i + 1) % inner.length] as Vec2 }));
}

export function rectangleWallGeometry(b: RectPlacement, t: number): WallGeometry[] {
  const hw = b.width / 2;
  const hd = b.depth / 2;
  const local: [Vec2, Vec2][] = [
    [
      { x: -hw, y: -hd + t / 2 },
      { x: hw, y: -hd + t / 2 },
    ],
    [
      { x: hw - t / 2, y: -hd + t },
      { x: hw - t / 2, y: hd - t },
    ],
    [
      { x: hw, y: hd - t / 2 },
      { x: -hw, y: hd - t / 2 },
    ],
    [
      { x: -hw + t / 2, y: hd - t },
      { x: -hw + t / 2, y: -hd + t },
    ],
  ];
  const placement = { position: b.center, rotation: b.rotation };
  return local.map(([s, e]) => ({
    start: localToWorld(s, placement),
    end: localToWorld(e, placement),
  }));
}

/** Whether the office's exterior wall along edge `edge` of storey `level` was removed on purpose. */
export function storeyWallRemoved(b: Building, level: number, edge: number): boolean {
  return (
    (level === 0 && b.omittedWalls.includes(edge)) ||
    (b.office?.openFloors.some((f) => f.wall === edge && f.level === level) ?? false)
  );
}

/**
 * Wall centrelines of an office storey. Next to a removed wall the neighbouring walls end at
 * that wall's inner face (where the slab ends too) instead of running on into the corner.
 */
export function officeStoreyWallGeometry(b: Building, level: number): WallGeometry[] {
  const walls = storeyWallGeometry(b, level);
  const t = b.wallThickness;
  const outline = ensureCounterClockwise(storeyCorners(b, level));
  const n = outline.length;
  if (walls.length !== n) return walls;
  const removed = (i: number) => storeyWallRemoved(b, level, i);
  /** Inner face of the wall along edge i: the edge moved inwards by the wall thickness. */
  const innerFace = (i: number) => {
    const a = outline[i] as Vec2;
    const c = outline[(i + 1) % n] as Vec2;
    const len = Math.hypot(c.x - a.x, c.y - a.y) || 1;
    const dir = { x: (c.x - a.x) / len, y: (c.y - a.y) / len };
    return { p: { x: a.x - dir.y * t, y: a.y + dir.x * t }, dir };
  };
  /** Point where the centreline through `from`→`to` crosses the inner face of edge i. */
  const cut = (from: Vec2, to: Vec2, i: number): Vec2 | null => {
    const f = innerFace(i);
    const d = { x: to.x - from.x, y: to.y - from.y };
    const det = d.x * f.dir.y - d.y * f.dir.x;
    if (Math.abs(det) < 1e-9) return null;
    const s = ((f.p.x - from.x) * f.dir.y - (f.p.y - from.y) * f.dir.x) / det;
    return s > 0 && s < 1 ? { x: from.x + d.x * s, y: from.y + d.y * s } : null;
  };
  return walls.map((g, i) => {
    const prev = (i - 1 + n) % n;
    const next = (i + 1) % n;
    const start = (removed(prev) && cut(g.start, g.end, prev)) || g.start;
    const end = (removed(next) && cut(g.end, start, next)) || g.end;
    return { start, end };
  });
}

/**
 * Exterior walls of a building: for a hall one per side over the full height; for an office
 * one per side and storey (each storey's walls follow its outline), except the pieces removed
 * on purpose.
 */
export function createBuildingWalls(b: Building): Wall[] {
  const o = b.office;
  if (o) {
    const e = storeyElevations(o);
    return o.floors.flatMap((_, level) =>
      officeStoreyWallGeometry(b, level).flatMap((g, edge): Wall[] => {
        if (storeyWallRemoved(b, level, edge)) return [];
        return [
          {
            id: storeyWallId(b.id, level, edge),
            start: g.start,
            end: g.end,
            thickness: b.wallThickness,
            height: (e[level + 1] ?? b.eaveHeight) - (e[level] ?? 0),
            baseElevation: 0,
            level,
            kind: 'exterior',
            cladding: 'plain',
            ownerId: b.id,
            color: o.floors[level]?.color ?? b.color,
            locked: false,
          },
        ];
      }),
    );
  }
  return buildingWallGeometry(b).map((g, i) => ({
    id: buildingWallId(b.id, i),
    start: g.start,
    end: g.end,
    thickness: b.wallThickness,
    height: b.eaveHeight,
    baseElevation: 0,
    level: 0,
    kind: 'exterior',
    cladding: 'plain',
    ownerId: b.id,
    color: b.color,
    locked: false,
  }));
}

export interface NewBuildingInput {
  readonly id: string;
  readonly name: string;
  readonly center: Vec2;
  readonly width: number;
  readonly depth: number;
  readonly eaveHeight: number;
  readonly roofType: RoofType;
  readonly rotation?: number;
  readonly roofPitchDeg?: number;
  readonly wallThickness?: number;
  readonly color?: string;
}

export const DEFAULT_ROOF_PITCH: Record<RoofType, number> = { gable: 8, mono: 5, flat: 0 };

/**
 * Pitches set by hand stay within these (degrees): sandwich roof panels need about 3°, and
 * steeper than 20° (gable) or 15° (mono) hardly ever pays off for a hall.
 */
export const ROOF_PITCH_LIMITS: Record<RoofType, readonly [number, number]> = {
  gable: [3, 20],
  mono: [3, 15],
  flat: [0, 0],
};
/** Gable halls wider than this get a flatter roof by default, so the ridge stays moderate. */
export const WIDE_SPAN = 25;

export const clampRoofPitch = (type: RoofType, deg: number): number => {
  const [min, max] = ROOF_PITCH_LIMITS[type];
  return Math.min(max, Math.max(min, deg));
};

/** Usual pitch for a new hall: 8° gable (6° above 25 m span), 5° mono-pitch, flat 0°. */
export const defaultRoofPitch = (type: RoofType, span: number): number =>
  type === 'gable' && span > WIDE_SPAN ? 6 : DEFAULT_ROOF_PITCH[type];

export function buildBuilding(input: NewBuildingInput): Building {
  return {
    id: input.id,
    name: input.name,
    kind: 'hall',
    center: input.center,
    rotation: input.rotation ?? 0,
    width: input.width,
    depth: input.depth,
    eaveHeight: input.eaveHeight,
    roofType: input.roofType,
    roofPitchDeg:
      input.roofPitchDeg ?? defaultRoofPitch(input.roofType, Math.min(input.width, input.depth)),
    wallThickness: input.wallThickness ?? DEFAULT_WALL_THICKNESS,
    color: input.color ?? DEFAULT_BUILDING_COLOR,
    ...DEFAULT_FACADE,
    office: null,
    footprint: null,
    eaveEdge: null,
    omittedWalls: [],
    locked: false,
  };
}

export interface NewOutlinedHallInput {
  readonly id: string;
  readonly name: string;
  /** Outer face of the walls, point by point (any direction; stored counter-clockwise). */
  readonly outline: readonly Vec2[];
  readonly eaveHeight: number;
  readonly roofType: Exclude<RoofType, 'gable'>;
  readonly roofPitchDeg?: number;
  readonly wallThickness?: number;
}

/**
 * Hall with a drawn outline (e.g. a trapezoid traced from a site plan). A mono-pitch roof
 * starts low along the longest edge until another eave side is chosen.
 */
export function buildOutlinedHall(input: NewOutlinedHallInput): Building {
  const footprint = ensureCounterClockwise(input.outline);
  const box = outlineBox(footprint);
  const lengths = footprint.map((p, i) => {
    const q = footprint[(i + 1) % footprint.length] as Vec2;
    return Math.hypot(q.x - p.x, q.y - p.y);
  });
  const longest = lengths.indexOf(Math.max(...lengths));
  return {
    ...buildBuilding({
      id: input.id,
      name: input.name,
      center: box.center,
      width: box.width,
      depth: box.depth,
      eaveHeight: input.eaveHeight,
      roofType: input.roofType,
      ...(input.roofPitchDeg !== undefined ? { roofPitchDeg: input.roofPitchDeg } : {}),
      ...(input.wallThickness !== undefined ? { wallThickness: input.wallThickness } : {}),
    }),
    footprint,
    eaveEdge: input.roofType === 'mono' ? longest : null,
  };
}
